import { LOGBOOK_WINDOWS, bucketForWindow } from './logbook';
import type { LogbookAggregationQuery, LogbookBucketMs, LogbookOutcome, LogbookQuery } from '../types/logbook';

/**
 * The investigation filter state, and the translation into the two query shapes Loglibrarian takes.
 *
 * Filters combine with AND upstream, so the state is deliberately flat: what a moderator sees in the
 * panel is exactly what is sent. Empty values are dropped rather than sent as blanks, because the
 * service validates strictly and a blank string is not the same as an absent filter.
 *
 * The whole state round-trips through the URL, so a search is a link. Handing a colleague the exact
 * window, region and action set matters more in moderation than almost anywhere else in Argus.
 */

export interface LogbookFilterState {
  /** A preset id from LOGBOOK_WINDOWS, or 'custom' when explicit bounds are set. */
  windowId: string;
  after?: number;
  before?: number;
  servers: string[];
  cluster: string;
  actorName: string;
  actorId: string;
  /** Exact action names. Up to 32 are sent as a comma list. */
  actions: string[];
  outcome: LogbookOutcome | '';
  dimension: string;
  worldId: string;
  objectId: string;
  namespace: string;
  box: [number, number, number, number, number, number] | null;
  limit: number;
}

export const DEFAULT_LOGBOOK_FILTERS: LogbookFilterState = {
  windowId: '24h',
  servers: [],
  cluster: '',
  actorName: '',
  actorId: '',
  actions: [],
  outcome: '',
  dimension: '',
  worldId: '',
  objectId: '',
  namespace: '',
  box: null,
  limit: 50,
};

/** Resolved absolute bounds for the selected window. `after` is inclusive, `before` exclusive. */
export function resolveWindow(state: LogbookFilterState, now = Date.now()): { after?: number; before?: number; spanMs: number | null } {
  if (state.windowId === 'custom') {
    const spanMs = state.after != null && state.before != null ? state.before - state.after : null;
    return { after: state.after, before: state.before, spanMs };
  }
  const preset = LOGBOOK_WINDOWS.find((entry) => entry.id === state.windowId) ?? LOGBOOK_WINDOWS[2];
  if (preset.ms == null) return { spanMs: null };
  return { after: now - preset.ms, before: now, spanMs: preset.ms };
}

function trimmed(value: string): string | undefined {
  const cleaned = value.trim();
  return cleaned.length > 0 ? cleaned : undefined;
}

/** The filters shared by every query shape. */
function baseQuery(state: LogbookFilterState, now = Date.now()): LogbookQuery {
  const { after, before } = resolveWindow(state, now);
  const query: LogbookQuery = {};
  if (state.servers.length > 0) query.servers = state.servers;
  if (trimmed(state.cluster)) query.cluster = state.cluster.trim();
  if (trimmed(state.worldId)) query.world_id = state.worldId.trim();
  if (trimmed(state.actorId)) query.actor_id = state.actorId.trim();
  if (trimmed(state.actorName)) query.actor_name = state.actorName.trim();
  if (state.actions.length > 0) query.action = state.actions.slice(0, 32).join(',');
  if (state.outcome) query.outcome = state.outcome;
  if (trimmed(state.dimension)) query.dimension = state.dimension.trim();
  if (trimmed(state.objectId)) query.object_id = state.objectId.trim();
  if (trimmed(state.namespace)) query.namespace = state.namespace.trim();
  if (state.box) query.box = state.box;
  if (after != null) query.after = Math.floor(after);
  if (before != null) query.before = Math.floor(before);
  return query;
}

export function toSearchQuery(state: LogbookFilterState, now = Date.now()): LogbookQuery {
  return { ...baseQuery(state, now), limit: state.limit };
}

export function toAggregationQuery(
  state: LogbookFilterState,
  groupBy: LogbookAggregationQuery['group_by'],
  now = Date.now(),
): LogbookAggregationQuery {
  const query: LogbookAggregationQuery = { ...baseQuery(state, now), group_by: groupBy };
  if (groupBy === 'time') {
    query.bucket_ms = bucketForWindow(resolveWindow(state, now).spanMs) as LogbookBucketMs;
  }
  return query;
}

/** True when nothing but the default window is set, which is worth saying in the empty state. */
export function isUnfiltered(state: LogbookFilterState): boolean {
  return state.servers.length === 0 && state.actions.length === 0 && !state.cluster && !state.actorId
    && !state.actorName && !state.outcome && !state.dimension && !state.worldId && !state.objectId
    && !state.namespace && !state.box;
}

export function activeFilterCount(state: LogbookFilterState): number {
  let count = 0;
  if (state.servers.length > 0) count += 1;
  if (state.cluster) count += 1;
  if (state.actions.length > 0) count += 1;
  if (state.actorId || state.actorName) count += 1;
  if (state.outcome) count += 1;
  if (state.dimension) count += 1;
  if (state.worldId) count += 1;
  if (state.objectId) count += 1;
  if (state.namespace) count += 1;
  if (state.box) count += 1;
  return count;
}

// ── URL round-trip ──────────────────────────────────────────────────────────

const LIST_KEYS = ['servers', 'actions'] as const;
const TEXT_KEYS = ['cluster', 'actorName', 'actorId', 'dimension', 'worldId', 'objectId', 'namespace', 'outcome'] as const;

export function filtersToParams(state: LogbookFilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (state.windowId !== DEFAULT_LOGBOOK_FILTERS.windowId) params.set('window', state.windowId);
  if (state.windowId === 'custom') {
    if (state.after != null) params.set('after', String(state.after));
    if (state.before != null) params.set('before', String(state.before));
  }
  for (const key of LIST_KEYS) {
    if (state[key].length > 0) params.set(key, state[key].join(','));
  }
  for (const key of TEXT_KEYS) {
    if (state[key]) params.set(key, state[key]);
  }
  if (state.box) params.set('box', state.box.join(','));
  if (state.limit !== DEFAULT_LOGBOOK_FILTERS.limit) params.set('limit', String(state.limit));
  return params;
}

function parseBox(value: string | null): LogbookFilterState['box'] {
  if (!value) return null;
  const parts = value.split(',').map(Number);
  if (parts.length !== 6 || parts.some((part) => !Number.isFinite(part))) return null;
  const [minX, minY, minZ, maxX, maxY, maxZ] = parts;
  return [
    Math.min(minX, maxX), Math.min(minY, maxY), Math.min(minZ, maxZ),
    Math.max(minX, maxX), Math.max(minY, maxY), Math.max(minZ, maxZ),
  ];
}

export function filtersFromParams(params: URLSearchParams): LogbookFilterState {
  const state: LogbookFilterState = { ...DEFAULT_LOGBOOK_FILTERS, servers: [], actions: [], box: null };
  const windowId = params.get('window');
  if (windowId && (windowId === 'custom' || LOGBOOK_WINDOWS.some((entry) => entry.id === windowId))) {
    state.windowId = windowId;
  }
  const after = Number(params.get('after'));
  const before = Number(params.get('before'));
  if (Number.isFinite(after) && after > 0) state.after = after;
  if (Number.isFinite(before) && before > 0) state.before = before;
  for (const key of LIST_KEYS) {
    const raw = params.get(key);
    if (raw) state[key] = raw.split(',').map((part) => part.trim()).filter(Boolean).slice(0, 64);
  }
  for (const key of TEXT_KEYS) {
    const raw = params.get(key);
    if (raw) (state as unknown as Record<string, string>)[key] = raw.slice(0, 512);
  }
  state.box = parseBox(params.get('box'));
  const limit = Number(params.get('limit'));
  if (Number.isInteger(limit) && limit >= 1 && limit <= 200) state.limit = limit;
  return state;
}

/** A cube centred on a position, for "everything that happened around here". */
export function boxAround(x: number, y: number, z: number, radius: number): LogbookFilterState['box'] {
  const clamp = (value: number) => Math.max(-32000000, Math.min(32000000, Math.round(value)));
  return [
    clamp(x - radius), clamp(y - radius), clamp(z - radius),
    clamp(x + radius), clamp(y + radius), clamp(z + radius),
  ];
}
